/** tm/legal/LegalAlertsPanel.tsx — 🔔 새 소식 패널: 법령 개정 · 국회 법안 · 국무회의 의결 · 입법예고 · 판례·해석 묶음(한 줄 미끼 문구) · 모두 확인 · PC 알림 */
import { useEffect, useRef, useState } from 'react';
import { C, T, R } from '../components/ui';
import { btn, whenLabel, MUTED } from './legalTheme';
import { SourceBadge, ItemTitle } from './legalUi';
import { badgeOf, hookOf, type AlertEntry } from './alertUtil';
import PcNotifyToggle from './PcNotifyToggle';
import type { AlertKey, LegalAlerts } from './legalTypes';

interface Props {
  data: LegalAlerts; mobile: boolean;
  onOpen: (e: AlertEntry) => void; onReadAll: () => Promise<void>; onClose: () => void;
  onToast: (m: string, k?: 'ok' | 'error') => void;
}

const ORDER: AlertKey[] = ['law', 'bill', 'cabinet', 'notice', 'legal'];
const LABEL: Record<AlertKey, string> = { law: '법령 개정', bill: '국회 법안', cabinet: '국무회의 의결', notice: '입법예고', legal: '판례·해석' };

export default function LegalAlertsPanel({ data, mobile, onOpen, onReadAll, onClose, onToast }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const down = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || ref.current?.contains(t) || t.closest('[data-lw-bell]')) return;
      onClose();
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [onClose]);
  useEffect(() => { ref.current?.focus(); }, []);

  const groups = ORDER.map(k => data.groups.find(g => g.key === k)).filter((g): g is NonNullable<typeof g> => !!g && g.items.length > 0);
  const readAll = async () => { setBusy(true); try { await onReadAll(); } finally { setBusy(false); } };

  return (
    <div ref={ref} role="dialog" aria-label="새 소식" tabIndex={-1} style={{
      position: 'absolute', top: 'calc(100% + 6px)', right: mobile ? 8 : 16, width: mobile ? 'calc(100vw - 16px)' : 420,
      maxWidth: 'calc(100vw - 16px)', maxHeight: mobile ? 'calc(100vh - 140px)' : '72vh', overflowY: 'auto', background: C.white,
      border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: '0 12px 32px rgba(17,24,39,.16)', zIndex: 60, outline: 'none',
    }}>
      <div style={{ position: 'sticky', top: 0, background: C.white, padding: '10px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
        <b style={{ fontSize: T.md, color: C.text }}>새 소식 {data.total > 0 && <span style={{ color: C.red, fontVariantNumeric: 'tabular-nums' }}>{data.total}</span>}</b>
        {data.total > 0 && <button type="button" className="lw-f" disabled={busy} onClick={readAll} style={{ ...btn(), opacity: busy ? 0.6 : 1, minHeight: mobile ? 40 : undefined }}>모두 확인</button>}
      </div>
      {groups.length === 0 && (
        <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: T.sm, color: MUTED, lineHeight: 1.7 }}>
          새로 확인할 소식이 없습니다.<br />매일 새벽 4시에 새로 모읍니다.
        </div>
      )}
      {groups.map(g => {
        const n = g.count ?? g.items.length;
        return (
          <div key={g.key}>
            <div style={{ padding: '10px 14px 4px', fontSize: T.sm, fontWeight: 600, color: C.sub, display: 'flex', gap: 6 }}>
              {LABEL[g.key] || g.label} <span style={{ color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
              {n > g.items.length && <span style={{ marginLeft: 'auto', fontSize: T.sm, color: MUTED, fontWeight: 400 }}>최근 {g.items.length}건</span>}
            </div>
            {g.items.map(it => {
              const e: AlertEntry = { it, key: g.key };
              const b = badgeOf(e);
              return (
                <button key={it.id} type="button" className="lw-f lw-row" onClick={() => onOpen(e)} style={{
                  display: 'block', width: '100%', textAlign: 'left', border: 0, borderBottom: `1px solid ${C.bg}`, background: 'transparent',
                  padding: '9px 14px', cursor: 'pointer', fontFamily: 'inherit', opacity: it.minor ? 0.65 : 1, minHeight: 44,
                }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3 }}>
                    <SourceBadge group={b.group} label={b.label} />
                    {it.topic && <span style={{ fontSize: T.sm, color: C.sub }}>{it.topic}</span>}
                    <span style={{ marginLeft: 'auto', fontSize: T.sm, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{whenLabel(it.first_seen_at)}</span>
                  </div>
                  <div className="lw-clamp" style={{ fontSize: T.md, fontWeight: 700, color: C.text, lineHeight: 1.45 }}><ItemTitle title={it.title} custom={it.custom_title} /></div>
                  <div style={{ fontSize: T.sm, color: C.sub, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hookOf(e)}</div>
                </button>
              );
            })}
          </div>
        );
      })}
      <div style={{ padding: '8px 14px', fontSize: T.sm, color: MUTED, borderTop: `1px solid ${C.border}`, background: C.bgAlt, borderRadius: `0 0 ${R.md}px ${R.md}px`, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span>뉴스 {data.newsToday}건은 알림 없이 주제별 뉴스 탭에 쌓입니다</span>
        <PcNotifyToggle compact onToast={onToast} />
      </div>
    </div>
  );
}
